import { useContext } from 'react';
import styled from '@emotion/styled';

import { tokens } from '@hotjar/hotsauce';

import { LabelButton } from './LabelButton';
import { useAvailableLabels } from '../hooks/use-available-labels';
import { LegacyContext } from '../context/global-context';

const DEFAULT_LABELS_NUMBER = 6;
const LABEL_SIZE = 32;
const LABEL_ADDED_TO_HIGHLIGHT = 'label_added_to_highlight';

type LabelId = string;

const List = styled.ul`
  background-color: ${tokens.color.neutral_200};
  border-radius: ${tokens.radius.l};

  display: inline-grid;
  grid-column-gap: ${tokens.spacer.x0};
  grid-template-columns: repeat(6, min-content);
  list-style-type: none;
  margin: 0;
  padding: ${tokens.spacer.x1};
`;

const Item = styled.li`
  margin: 0;
  padding: 0;
  height: ${LABEL_SIZE}px;
`;

const LabelPlaceholder = styled.span`
  display: block;
  height: ${LABEL_SIZE}px;
  width: ${LABEL_SIZE}px;
`;

interface LabelsEditorProps {
  className?: string;
  onAdd?: (addedLabelId: LabelId) => void;
  onRemove?: (removedLabelId: LabelId) => void;
  onSelectedIdsChange: (selectedLabelIds: Set<LabelId>) => void;
  selectedLabelIds: Set<LabelId>;
  siteId: Parameters<typeof useAvailableLabels>[0];
}

export function LabelsEditor({
  className,
  onAdd,
  onRemove,
  onSelectedIdsChange,
  selectedLabelIds,
  siteId,
}: LabelsEditorProps): JSX.Element | null {
  const { event } = useContext(LegacyContext);

  const createAddHandler = (addedId: LabelId) => () => {
    const newSelectedLabelIds = new Set(selectedLabelIds);
    newSelectedLabelIds.add(addedId);
    onSelectedIdsChange(newSelectedLabelIds);

    if (event !== undefined) {
      event(LABEL_ADDED_TO_HIGHLIGHT);
    }

    if (onAdd) {
      onAdd(addedId);
    }
  };
  const createRemoveHandler = (removedId: LabelId) => () => {
    const newSelectedLabelIds = new Set(selectedLabelIds);
    newSelectedLabelIds.delete(removedId);
    onSelectedIdsChange(newSelectedLabelIds);

    if (onRemove) {
      onRemove(removedId);
    }
  };

  const { data: labels } = useAvailableLabels(siteId);

  if (typeof labels === 'undefined') {
    return (
      <List className={className}>
        {Array.from({ length: DEFAULT_LABELS_NUMBER }).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Item key={index}>
            <LabelPlaceholder />
          </Item>
        ))}
      </List>
    );
  }

  const labelsWithState = labels.map((label) => ({
    ...label,
    isSelected: selectedLabelIds.has(label.id),
  }));

  return (
    <List className={className}>
      {labelsWithState.map(({ id, ...labelWithState }) => (
        <Item key={id}>
          <LabelButton
            {...labelWithState}
            id={id}
            onAdd={createAddHandler(id)}
            onRemove={createRemoveHandler(id)}
            size={LABEL_SIZE}
          />
        </Item>
      ))}
    </List>
  );
}
